import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { TABLE_COLUMNS, TABLE_COLUMN_ORDER, TABLE_FILTERS } from "./Data/DownloadsTableColumns";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { getDownloadHistory } from "../../Api/DataDownloadApi";
import CustomIcon from "../../Components/CustomIcon";
import DownloadButton from "./DownloadButton";
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { usePagesContent } from "../../Hooks/usePagesContent";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import AppCache from "../../Helper/Common/AppCache";

const TABLE_ID = "DownloadsTable";

const DownloadsTable = ({ onNewDownload }) => {
  const pageContent = usePagesContent();

  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [currentTableColumns, setCurrentTableColumns] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sorting, setSorting] = useState([{ sortBy: "createdAt", order: "desc" }]);

  const pageCount = 20;

  const customRenderers = useMemo(() => ({
    status_custom: (record) => {
      const status = record.status || "";
      const configs = {
        SUCCESS:     { color: "#008A15", bgColor: "#E8F5E9", label: "Success",     icon: <CheckCircleOutlineRoundedIcon sx={{ color: "#008A15", fontSize: "20px" }} /> },
        IN_PROGRESS: { color: "#1565C0", bgColor: "#E3F2FD", label: "In Progress", icon: <CustomIcon name="round-clock-icon" width="20" height="20" stroke="#1565C0" /> },
        OPEN:        { color: "#DCA101", bgColor: "#FFF8E1", label: "Open",         icon: <CustomIcon name="round-clock-icon" width="20" height="20" stroke="#DCA101" /> },
        FAILED:      { color: "#C62828", bgColor: "#FFEBEE", label: "Failed",       icon: <CancelOutlinedIcon sx={{ color: "#C62828", fontSize: "20px" }} /> },
      };
      const cfg = configs[status] || { color: "#616161", bgColor: "#F5F5F5", label: status, icon: null };
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "fit-content" }}>
          <Box sx={{ display: "flex", alignItems: "center", color: cfg.color }}>{cfg.icon}</Box>
          <Typography variant="fw500s14lh20c2E2E2E" sx={{ fontSize: "14px", lineHeight: "20px" }}>
            {cfg.label}
          </Typography>
        </Box>
      );
    },
    download_action: (record) => {
      if (record.status !== "SUCCESS" || !record.downloadURL) return null;
      return <DownloadButton downloadURL={record.downloadURL} urlExpiresAt={record.urlExpiresAt} />;
    },
  }), []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        ...selectedFilters,
        rowsPerPage: pageCount,
        pageNumber,
        ...(sorting?.[0] && { sortBy: sorting[0].sortBy, order: sorting[0].order }),
      };
      const res = await getDownloadHistory(params);
      setTableData(res?.response?.data?.records || []);
      setPageInfo({ total: res?.response?.data?.totalRecords || 0 });
    } catch (error) {
      console.error("Error fetching download history:", error);
      setTableData([]);
      setPageInfo({});
    } finally {
      setIsLoading(false);
    }
  };

  const setupTableColumns = () => {
    const tableInfo = pageContent?.metaInfo?.table?.dataDownloadHistoryTable || {};
    const tableColumns = tableInfo?.columnsConfig || TABLE_COLUMNS;
    const tableColumnOrder = tableInfo?.columnOrdering || TABLE_COLUMN_ORDER;

    const available = getColumnsConfig({ tableColumns, tableColumnOrder, customRenderers });
    setAvailableColumns(available);

    const defaultCols = Object.keys(tableColumns)
      .sort((a, b) => (a.fixed && !b.fixed ? -1 : !a.fixed && b.fixed ? 1 : 0))
      .slice(0, MAX_COLUMN_SELECTION_COUNT);

    setSelectedColumns(defaultCols);
    setDefaultSelectedColumns(defaultCols);

    const currentCols = available.filter((col) => Object.keys(tableColumns).includes(col.key));
    setCurrentTableColumns(currentCols);
    setColumnOrder(currentCols.map((col) => col.headerLabel));
  };

  const setupTableFilters = async () => {
    const filtersConfig =
      pageContent?.metaInfo?.filterConfigurations?.dataDownloadHistoryTable?.filters ||
      TABLE_FILTERS;
    const filtersObj = await fetchFiltersDataObject(filtersConfig, availableFilters);
    setAvailableFilters(filtersObj);
  };

  const applySelectedColumns = (newSelectedColumns) => {
    AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns });
    setSelectedColumns(newSelectedColumns);
  };

  const applySelectedFilters = (filters) => {
    setSelectedFilters(filters);
    setPageNumber(1);
  };

  const onColumnOrderChange = (cols) => setColumnOrder(cols.map((c) => c.headerLabel));
  const onPageChange = (pageNo) => setPageNumber(pageNo);
  const applySort = (key, order) => { setSorting([{ sortBy: key, order }]); setPageNumber(1); };

  useEffect(() => {
    setupTableColumns();
    setupTableFilters();
  }, [pageContent]);

  useEffect(() => {
    const currentCols = availableColumns.filter((col) => selectedColumns.includes(col.key));
    setCurrentTableColumns(currentCols);
  }, [selectedColumns, availableColumns]);

  useEffect(() => {
    pageNumber === 1 ? fetchData() : setPageNumber(1);
  }, [selectedFilters, sorting]);

  useEffect(() => {
    fetchData();
  }, [pageNumber]);

  return (
    <Box sx={{ mb: 3, width: "calc(100vw - 102px)" }}>
      <GenericDraggableTableView
        title="Download History"
        tableID={TABLE_ID}
        showPrimaryButton={false}
        disableColumnSettings={false}
        applySearch={() => {}}
        disableSearch={true}
        disableDownloads={true}
        filters={availableFilters}
        selectedFilters={selectedFilters}
        applyFilters={applySelectedFilters}
        initialColumns={availableColumns}
        tableColumns={currentTableColumns}
        selectedColumns={selectedColumns}
        applySelectedColumns={applySelectedColumns}
        tableData={tableData}
        onColumnOrderChange={onColumnOrderChange}
        columnOrder={columnOrder}
        isLoading={isLoading}
        pageNumber={pageNumber}
        rowsPerPage={pageCount}
        count={pageInfo?.total || 0}
        total={pageInfo?.total || 0}
        applySort={applySort}
        onPageChange={onPageChange}
        emptyMessage="No download requests found"
        customRightActions={true}
        customRightActionsView={(
          <Box sx={{ ml: 1 }}>
            <PrimaryFormLoadingActionButton onClick={onNewDownload} startIcon={<AddIcon />}>
              New Download
            </PrimaryFormLoadingActionButton>
          </Box>
        )}
        maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
        defaultSelectedColumns={defaultSelectedColumns}
      />
    </Box>
  );
};

export default DownloadsTable;
